import { NoddiSelectableBox } from '@ui-components/Inputs/NoddiSelectableBox';
import { TanstackFieldError } from '../TanstackFieldError';
import { useFieldContext } from '../formContext';

type Value = string | number;

type Option<TValue extends Value> = {
  value: TValue;
  label: string;
};

type Props<TValue extends Value> = {
  options: Option<TValue>[];
  helperText?: string;
  disabledValues?: TValue[];
  renderLabel?: (option: Option<TValue>) => React.ReactNode;
};

const getNextValues = <TValue extends Value>({
  values,
  optionValue,
  isSelected
}: {
  values: TValue[];
  optionValue: TValue;
  isSelected: boolean;
}) => {
  if (isSelected) {
    return [...values, optionValue];
  }

  return values.filter((value) => value !== optionValue);
};

export const TanstackFormSelectableBoxMultiSelect = <TValue extends Value>({
  options,
  helperText,
  disabledValues,
  renderLabel
}: Props<TValue>) => {
  const field = useFieldContext<TValue[]>();
  const values = field.state.value ?? [];

  return (
    <div className='flex flex-col gap-2'>
      {helperText ? <p className='text-sm text-muted-foreground'>{helperText}</p> : null}
      {options.map((option) => {
        const isSelected = values.includes(option.value);
        const isDisabled = disabledValues?.includes(option.value) ?? false;
        return (
          <NoddiSelectableBox
            key={option.value}
            isSelected={isSelected}
            setIsSelected={(nextIsSelected) => {
              field.handleChange(
                getNextValues({
                  values,
                  optionValue: option.value,
                  isSelected: nextIsSelected
                })
              );
            }}
            disabled={isDisabled}
          >
            {renderLabel ? renderLabel(option) : option.label}
          </NoddiSelectableBox>
        );
      })}
      <TanstackFieldError field={field} />
    </div>
  );
};
